/**
 * The page a standalone customer meets when something is wrong at the boundary.
 *
 * Requirement 5 of the slice, in one component: the module refuses to fail open, so a
 * service with nothing configured refuses every tenanted route no matter what token is
 * presented. The shell has to render that as a **configuration message**, not as an
 * infinite login loop — which means naming the server-side setting that is missing, and
 * saying plainly that signing in again cannot help.
 *
 * The other kinds are here for the same reason: each one is a different instruction, and a
 * single "something went wrong" screen would collapse three of them into a wrong one.
 */

import { Alert, AlertTitle, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { SHELL_CONFIG } from '../config';
import type { Fault } from '../auth/diagnose';

function Code({ children }: { children: string }) {
  return (
    <Box
      component="code"
      sx={{
        px: 0.6,
        py: 0.15,
        borderRadius: 0.75,
        bgcolor: 'action.hover',
        fontSize: 12.5,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      }}
    >
      {children}
    </Box>
  );
}

function UnconfiguredBody() {
  return (
    <Stack spacing={1.5}>
      <Typography sx={{ fontSize: 13 }}>
        The service starts, serves <Code>/health</Code>, and logs{' '}
        <Code>auth.not_configured</Code>. It then refuses every tenanted route, because
        the alternative — serving data to a request whose claims nobody verified — is not
        a failure mode this module has.
      </Typography>
      <Divider />
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Configure the service</Typography>
      <Typography sx={{ fontSize: 13 }} component="div">
        Production wants all three:
        <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
          <li>
            <Code>INVENTORY_AUTH_JWKS_URL</Code> — the issuer&apos;s published key set
          </li>
          <li>
            <Code>INVENTORY_AUTH_ISSUER</Code> — must equal the token&apos;s <Code>iss</Code>
          </li>
          <li>
            <Code>INVENTORY_AUTH_AUDIENCE</Code> — must equal the token&apos;s{' '}
            <Code>aud</Code>; defaults to <Code>inai-inventory</Code>
          </li>
        </Box>
      </Typography>
      <Typography sx={{ fontSize: 13 }} component="div">
        For local development without an identity provider, the service takes a
        locally-signed HS256 token instead:
        <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
          <li>
            <Code>INVENTORY_AUTH_DEV_LOCAL_JWT=1</Code> and <Code>INVENTORY_JWT_SECRET=…</Code>{' '}
            on the service — both refused when <Code>INVENTORY_ENVIRONMENT=production</Code>
          </li>
          <li>
            <Code>VITE_AUTH_MODE=manual</Code> on this shell, which then asks for a token
            rather than redirecting anywhere
          </li>
        </Box>
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
        A deployment that supplies its own claims middleware — the demo scripts do — is
        also a valid configuration, and this message means that middleware is not
        installed on the process answering {SHELL_CONFIG.apiBaseUrl}.
      </Typography>
    </Stack>
  );
}

function TenantUnboundBody({ fault }: { fault: Fault }) {
  return (
    <Stack spacing={1.5}>
      <Typography sx={{ fontSize: 13 }}>
        The token was verified. It carries no claim naming an organization this service can
        serve, so the answer is <strong>403 and not 401</strong> — deliberately, because a
        401 would send a perfectly valid credential back to the issuer forever.
      </Typography>
      <Typography sx={{ fontSize: 13 }}>
        The service reads, in order: <Code>org</Code>, <Code>org_id</Code>, <Code>tenant</Code>,{' '}
        <Code>tenant_id</Code>. The first one <em>present</em> wins and there is no
        fall-through — a token carrying two of them with different values is a token nobody
        understands. The value must be a UUID.
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
        D-12 is half open: the issuer is settled, the literal claim name is not. See{' '}
        <RouterLink to="/diagnostics">diagnostics</RouterLink> for what this token actually
        carries.
      </Typography>
      {fault.message ? (
        <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
          The service said: {fault.message}
        </Typography>
      ) : null}
    </Stack>
  );
}

const TITLES: Record<Fault['kind'], string> = {
  service_unconfigured: 'This service is not configured to authenticate anyone',
  tenant_unbound: 'Your token is not bound to an organization',
  credential: 'Your session has ended',
  unavailable: 'The service cannot decide right now',
  unreachable: 'The inventory service did not answer',
  not_in_plan: 'Your plan does not include this',
  other: 'Something went wrong',
};

export interface FaultNoticeProps {
  fault: Fault;
  onRetry?: () => void;
  onSignOut?: () => void;
}

export function FaultNotice({ fault, onRetry, onSignOut }: FaultNoticeProps) {
  const severity = fault.kind === 'unavailable' ? 'warning' : 'error';

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', mt: 6, px: 3, pb: 6 }}>
      <Alert severity={severity} variant="outlined">
        <AlertTitle sx={{ fontWeight: 700 }}>{TITLES[fault.kind]}</AlertTitle>

        {fault.kind === 'service_unconfigured' ? (
          <UnconfiguredBody />
        ) : fault.kind === 'tenant_unbound' ? (
          <TenantUnboundBody fault={fault} />
        ) : (
          <Typography sx={{ fontSize: 13 }}>{fault.message}</Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
          {onRetry ? (
            <Button size="small" variant="contained" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          {onSignOut ? (
            <Button size="small" variant="outlined" onClick={onSignOut}>
              Sign out
            </Button>
          ) : null}
          <Button size="small" component={RouterLink} to="/diagnostics">
            Diagnostics
          </Button>
        </Stack>

        {fault.code ? (
          <Typography color="text.secondary" sx={{ fontSize: 11.5, mt: 1.5 }}>
            {fault.status ? `HTTP ${fault.status} · ` : ''}
            {fault.code}
          </Typography>
        ) : null}
      </Alert>
    </Box>
  );
}
